import React from "react";
import {
    Box, Stack, Typography, TextField, Select, MenuItem, Chip,
    Paper, Button, Divider, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton, Skeleton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Props:
 *   category?: "Projects" | "Brands" | "Channels" | "Issues" | "Misc"
 *   title?: string
 */
export default function HelpCenter({ category = null, title = "Help" }) {
    const [docs, setDocs] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    const [q, setQ] = React.useState("");
    const [selectedCategory, setSelectedCategory] = React.useState(category || "");
    const [openDoc, setOpenDoc] = React.useState(null); // full doc for dialog

    // simple debounce for search
    const [debouncedQ, setDebouncedQ] = React.useState(q);
    React.useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q), 350);
        return () => clearTimeout(t);
    }, [q]);

    const categories = ["Projects", "Brands", "Channels", "Issues", "Misc"];

    const loadDocs = React.useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params = [];
            if (selectedCategory) params.push(`category=${encodeURIComponent(selectedCategory)}`);
            if (debouncedQ) params.push(`q=${encodeURIComponent(debouncedQ)}`);
            const url = `/api/help${params.length ? "?" + params.join("&") : ""}`;
            const res = await fetch(url);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setDocs(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e.message || "Failed to load help");
        } finally {
            setLoading(false);
        }
    }, [selectedCategory, debouncedQ]);

    React.useEffect(() => {
        loadDocs().catch(console.error);
    }, [loadDocs]);

    const formatDate = (d) =>
        d ? new Date(d).toLocaleString() : "";

    const categoryColor = (cat) => {
        switch (cat) {
            case "Projects": return "primary";
            case "Brands":   return "success";
            case "Channels": return "info";
            case "Issues":   return "warning";
            default:         return "default";
        }
    };

    return (
        <Box sx={{ mx: "auto", maxWidth: 980, p: { xs: 2, md: 3 } }}>
            <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "start", md: "center" }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h4" fontWeight={700}>{title}</Typography>
                <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search help…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, opacity: 0.6 }} />
                        }}
                    />
                    <Select
                        size="small"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        displayEmpty
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value=""><em>All categories</em></MenuItem>
                        {categories.map((c) => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                    </Select>
                </Stack>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {error && (
                <Paper variant="outlined" sx={{ p: 2, borderColor: "error.light", bgcolor: "error.50", mb: 2 }}>
                    <Typography color="error">{error}</Typography>
                </Paper>
            )}

            {loading ? (
                <Stack spacing={1}>
                    {[...Array(4)].map((_, i) => (
                        <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                            <Skeleton variant="text" width="40%" />
                            <Skeleton variant="text" width="70%" />
                            <Skeleton variant="rectangular" height={10} sx={{ my: 1 }} />
                            <Skeleton variant="text" width="30%" />
                        </Paper>
                    ))}
                </Stack>
            ) : docs.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography>No help articles found.</Typography>
                </Paper>
            ) : (
                <Stack spacing={1.5}>
                    {docs.map((doc) => (
                        <Paper key={doc.id} variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h6" noWrap title={doc.title}>{doc.title}</Typography>
                                    {doc.excerpt && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {doc.excerpt}
                                        </Typography>
                                    )}
                                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                                        <Chip size="small" label={doc.category} color={categoryColor(doc.category)} variant="outlined" />
                                        <Typography variant="caption" color="text.secondary">
                                            Last updated {formatDate(doc.lastModified)}
                                        </Typography>
                                    </Stack>
                                </Box>
                                <Button variant="contained" onClick={() => setOpenDoc(doc)}>Read</Button>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            {/* Modal dialog for the full content */}
            <Dialog
                open={!!openDoc}
                onClose={() => setOpenDoc(null)}
                fullWidth
                maxWidth="md"
                scroll="paper"
            >
                <DialogTitle sx={{ pr: 6 }}>
                    <Typography variant="h6" component="div">{openDoc?.title}</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenDoc(null)}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                {openDoc && (
                    <>
                        <DialogContent dividers sx={{
                            "& h1, & h2, & h3": { mt: 2 },
                            "& pre": { bgcolor: "grey.100", p: 1.5, borderRadius: 1, overflow: "auto" },
                            "& code": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace" },
                            "& a": { textDecoration: "underline" }
                        }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <Chip size="small" label={openDoc.category} color={categoryColor(openDoc.category)} />
                                <Typography variant="caption" color="text.secondary">
                                    Last updated {formatDate(openDoc.lastModified)}
                                </Typography>
                            </Stack>

                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {openDoc.body || ""}
                            </ReactMarkdown>
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={() => setOpenDoc(null)}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
}
